import * as Sentry from "@sentry/react"
import {QueryClient, QueryClientProvider} from "@tanstack/react-query"
import {TRPCClientError} from "@trpc/react-query"
import {AnimatePresence} from "framer-motion"
import {FC, useCallback, useEffect, useMemo, useRef, useState} from "react"

import {SessionProvider} from "@frontend/session/provider"
import {trackEvent} from "@frontend/utils/trackEvent"
import {trpc} from "@frontend/utils/trpc"
import {TRPC_CLIENT} from "@frontend/utils/trpcClient"

import {ChatBubble} from "./ChatBubble"
import {ChatPanel} from "./ChatPanel"
import {ConversationStarters} from "./ConversationStarters"
import {getContextualStarters} from "./getContextualStarters"
import {applySlideoutLayout, removeSlideoutLayout} from "./slideoutLayout"
import {
  getInitialStarterBootstrapPayload,
  normalizeStarterBootstrapPayload,
} from "./starterBootstrap"
import {usePageContext} from "./usePageContext"

const QUERY_CLIENT = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error): boolean => {
        if (!(error instanceof TRPCClientError)) {
          return false
        }

        if (error.data?.code === "NOT_FOUND") {
          return false
        }

        return failureCount < 3
      },
    },
  },
})

const LOCAL_STORAGE_CASE_ID_KEY = "ri2-widget-caseId"
const LOCAL_STORAGE_STARTERS_DISMISSED_KEY = "ri2-widget-startersDismissed"
const HASH_CASE_ID_PARAM = "ri2-case"
const LAST_SEEN_MESSAGE_ID_KEY_PREFIX = "ri2-widget-lastSeenId:"
const LAST_UNREAD_MESSAGE_ID_KEY_PREFIX = "ri2-widget-lastUnreadId:"

const getLastSeenMessageIdKey = (caseId: string): string =>
  `${LAST_SEEN_MESSAGE_ID_KEY_PREFIX}${caseId}`

const getLastUnreadMessageIdKey = (caseId: string): string =>
  `${LAST_UNREAD_MESSAGE_ID_KEY_PREFIX}${caseId}`

const getHashCaseId = (): string | null => {
  const match = window.location.hash.match(/(?:^#|&)ri2-case=([^&]+)/)
  return match ? match[1] : null
}

const setHashCaseId = (caseId: string): void => {
  const existingHash = window.location.hash.replace(
    /(?:^#?|&?)ri2-case=[^&]*/,
    "",
  )
  const base = existingHash.replace(/^#/, "")
  const newHash = base
    ? `${base}&${HASH_CASE_ID_PARAM}=${caseId}`
    : `${HASH_CASE_ID_PARAM}=${caseId}`
  history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}#${newHash}`,
  )
}

const clearHashCaseId = (): void => {
  const newHash = window.location.hash
    .replace(/(?:^#?|&?)ri2-case=[^&]*/g, "")
    .replace(/^#?&/, "")
    .replace(/^#/, "")
  history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}${
      newHash ? `#${newHash}` : ""
    }`,
  )
}

const getIsInitialized = (): boolean => !!window.ri2?.getUserId

const useIsInitialized = (): boolean => {
  const [isInitialized, setIsInitialized] = useState(getIsInitialized())

  useEffect(() => {
    if (isInitialized) return

    const interval = setInterval(() => {
      if (getIsInitialized()) {
        setIsInitialized(true)
        clearInterval(interval)
      }
    }, 100)

    return (): void => {
      clearInterval(interval)
    }
  })

  return isInitialized
}

const getDisplayMode = (): "overlay" | "slideout" =>
  window.ri2?.displayMode === "slideout" ? "slideout" : "overlay"

export const ChatWidget: FC = () => {
  const isInitialized = useIsInitialized()
  const hasTrackedImpression = useRef(false)
  const hasTrackedStartersShown = useRef(false)
  const hasTrackedResumedFromLink = useRef(false)
  const latestAssistantMessageIdRef = useRef<string | null>(null)
  const lastUnreadMessageIdRef = useRef<string | null>(null)
  // Captured at mount so we can fire the resume event regardless of whether
  // the widget later rewrites the hash with its own caseId.
  const initialHashCaseIdRef = useRef<string | null>(null)
  if (initialHashCaseIdRef.current === null) {
    initialHashCaseIdRef.current = getHashCaseId()
  }
  const pageContext = usePageContext()
  // Read once — displayMode is stable for the lifetime of the widget mount.
  const [displayMode] = useState(getDisplayMode)
  const isSlideout = displayMode === "slideout"
  const startsExpanded = !window.ri2?.startCollapsed
  const hasRestoredCase = isSlideout || getHashCaseId() !== null
  const [isOpen, setIsOpen] = useState(() => startsExpanded && hasRestoredCase)
  const [hasEverOpened, setHasEverOpened] = useState(
    () => startsExpanded && hasRestoredCase,
  )
  const [unreadCount, setUnreadCount] = useState(0)
  const [caseId, setCaseId] = useState<string | null>(
    () => getHashCaseId() ?? localStorage.getItem(LOCAL_STORAGE_CASE_ID_KEY),
  )
  const [starterPayload, setStarterPayload] =
    useState<Ri2StarterBootstrapPayload | null>(() =>
      getInitialStarterBootstrapPayload(),
    )
  const [starterMessage, setStarterMessage] = useState<string | null>(
    () => getInitialStarterBootstrapPayload()?.starterMessage ?? null,
  )
  const [startersDismissed, setStartersDismissed] = useState(
    () => localStorage.getItem(LOCAL_STORAGE_STARTERS_DISMISSED_KEY) === "true",
  )

  // Compute contextual starters from the current page context
  const starters = useMemo(
    () => getContextualStarters(pageContext),
    [pageContext],
  )

  useEffect(() => {
    if (!isSlideout) return

    if (isOpen) {
      applySlideoutLayout()
    } else {
      removeSlideoutLayout()
    }

    return (): void => {
      removeSlideoutLayout()
    }
  }, [isOpen, isSlideout])

  useEffect(() => {
    if (!caseId) {
      latestAssistantMessageIdRef.current = null
      lastUnreadMessageIdRef.current = null
      setUnreadCount(0)
      return
    }

    lastUnreadMessageIdRef.current = localStorage.getItem(
      getLastUnreadMessageIdKey(caseId),
    )
    setUnreadCount(lastUnreadMessageIdRef.current ? 1 : 0)
  }, [caseId])

  useEffect(() => {
    if (caseId) {
      setHasEverOpened(true)
    }
  }, [caseId])

  const onOpen = useCallback(() => {
    setHasEverOpened(true)
    setIsOpen(true)
    setUnreadCount(0)
    if (caseId) {
      const latestAssistantMessageId = latestAssistantMessageIdRef.current
      if (latestAssistantMessageId) {
        localStorage.setItem(
          getLastSeenMessageIdKey(caseId),
          latestAssistantMessageId,
        )
      }
      localStorage.removeItem(getLastUnreadMessageIdKey(caseId))
      lastUnreadMessageIdRef.current = null
    }
    trackEvent("widget_chat_open")
    if (isSlideout) {
      window.dispatchEvent(new Event("ri2:userExpanded"))
    }
  }, [caseId, isSlideout])

  const onMinimize = useCallback(() => {
    setIsOpen(false)
    trackEvent("widget_chat_minimized")
    if (isSlideout) {
      window.dispatchEvent(new Event("ri2:userCollapsed"))
    }
  }, [isSlideout])

  const onClose = useCallback(() => {
    setIsOpen(false)
    trackEvent("widget_chat_closed")
    if (isSlideout) {
      window.dispatchEvent(new Event("ri2:userCollapsed"))
    }
  }, [isSlideout])

  // Let the host page collapse / expand the panel programmatically.
  useEffect(() => {
    const handleCollapse = (): void => {
      if (isOpen) {
        trackEvent("widget_chat_minimized")
        trackEvent("widget_chat_minimized_api")
      }
      setIsOpen(false)
    }
    const handleExpand = (event: Event): void => {
      const expandEvent = event as CustomEvent<
        Ri2StarterBootstrapPayload | string | null
      >
      const hostStarterPayload = normalizeStarterBootstrapPayload(
        expandEvent.detail ??
          window.ri2?.initialStarterPayload ??
          window.ri2?.initialMessage,
      )
      if (hostStarterPayload) {
        setStarterPayload(hostStarterPayload)
        setStarterMessage(hostStarterPayload.starterMessage)
      }

      if (!isOpen) {
        trackEvent("widget_chat_open")
        trackEvent("widget_chat_open_api")
      }
      setHasEverOpened(true)
      setIsOpen(true)
      setUnreadCount(0)
      if (caseId) {
        const latestAssistantMessageId = latestAssistantMessageIdRef.current
        if (latestAssistantMessageId) {
          localStorage.setItem(
            getLastSeenMessageIdKey(caseId),
            latestAssistantMessageId,
          )
        }
        localStorage.removeItem(getLastUnreadMessageIdKey(caseId))
        lastUnreadMessageIdRef.current = null
      }
    }

    window.addEventListener("ri2:collapse", handleCollapse)
    window.addEventListener("ri2:expand", handleExpand)
    return (): void => {
      window.removeEventListener("ri2:collapse", handleCollapse)
      window.removeEventListener("ri2:expand", handleExpand)
    }
  }, [caseId, isOpen])

  const onCaseCreated = useCallback((newCaseId: string) => {
    setCaseId(newCaseId)
    localStorage.setItem(LOCAL_STORAGE_CASE_ID_KEY, newCaseId)
    setHashCaseId(newCaseId)
  }, [])

  const onNewConversation = useCallback(() => {
    trackEvent("widget_chat_new_conversation")
    if (caseId) {
      localStorage.removeItem(getLastSeenMessageIdKey(caseId))
      localStorage.removeItem(getLastUnreadMessageIdKey(caseId))
    }
    latestAssistantMessageIdRef.current = null
    lastUnreadMessageIdRef.current = null
    setUnreadCount(0)
    setCaseId(null)
    localStorage.removeItem(LOCAL_STORAGE_CASE_ID_KEY)
    clearHashCaseId()
  }, [caseId])

  const onUnreadMessage = useCallback(
    (messageId: string) => {
      if (isOpen || !caseId || messageId === lastUnreadMessageIdRef.current) {
        return
      }

      lastUnreadMessageIdRef.current = messageId
      localStorage.setItem(getLastUnreadMessageIdKey(caseId), messageId)
      setUnreadCount((currentCount) => currentCount + 1)
    },
    [caseId, isOpen],
  )

  const onLatestAssistantMessageChange = useCallback(
    (messageId: string | null) => {
      latestAssistantMessageIdRef.current = messageId

      if (!caseId || !messageId || !isOpen) {
        return
      }

      localStorage.setItem(getLastSeenMessageIdKey(caseId), messageId)
      localStorage.removeItem(getLastUnreadMessageIdKey(caseId))
      lastUnreadMessageIdRef.current = null
    },
    [caseId, isOpen],
  )

  const onStarterSelect = useCallback((message: string) => {
    trackEvent("widget_chat_starter_selected")
    setStarterPayload({starterMessage: message})
    setStarterMessage(message)
    setHasEverOpened(true)
    setIsOpen(true)
    setUnreadCount(0)
    setStartersDismissed(true)
    trackEvent("widget_chat_open")
  }, [])

  const onStartersDismiss = useCallback(() => {
    trackEvent("widget_chat_starters_dismissed")
    localStorage.setItem(LOCAL_STORAGE_STARTERS_DISMISSED_KEY, "true")
    setStartersDismissed(true)
  }, [])

  const onStarterMessageConsumed = useCallback(() => {
    setStarterPayload(null)
    setStarterMessage(null)
    if (window.ri2) {
      window.ri2.initialMessage = undefined
      window.ri2.initialStarterPayload = undefined
    }
  }, [])

  const showStarters =
    !isSlideout &&
    !isOpen &&
    !startersDismissed &&
    !caseId &&
    starters.length > 0

  useEffect(() => {
    if (!isInitialized || hasTrackedImpression.current) return
    hasTrackedImpression.current = true
    trackEvent("widget_chat_impression")
  }, [isInitialized])

  // Fire once if the page was loaded with a case id already in the URL hash.
  // That hash is only ever placed there by `setHashCaseId` AFTER a case is
  // created in-session, so at mount time a non-null value means the user
  // arrived via a shared/saved link (e.g. from the Continue Later action).
  useEffect(() => {
    if (!isInitialized || hasTrackedResumedFromLink.current) return
    const initialCaseId = initialHashCaseIdRef.current
    if (!initialCaseId) return
    hasTrackedResumedFromLink.current = true
    trackEvent("widget_chat_resumed_from_link", initialCaseId)
  }, [isInitialized])

  useEffect(() => {
    if (!showStarters || hasTrackedStartersShown.current) return
    hasTrackedStartersShown.current = true
    trackEvent("widget_chat_starters_shown")
  }, [showStarters])

  if (!isInitialized) {
    return null
  }

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTags({error_boundary: "widget"})
      }}
    >
      <trpc.Provider client={TRPC_CLIENT} queryClient={QUERY_CLIENT}>
        <QueryClientProvider client={QUERY_CLIENT}>
          <SessionProvider>
            <AnimatePresence>
              {showStarters && (
                <ConversationStarters
                  key="starters"
                  starters={starters}
                  onSelect={onStarterSelect}
                  onDismiss={onStartersDismiss}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {!isOpen && (
                <ChatBubble
                  key="bubble"
                  onClick={onOpen}
                  unreadCount={unreadCount}
                />
              )}
            </AnimatePresence>
            {hasEverOpened && (
              <ChatPanel
                displayMode={displayMode}
                isOpen={isOpen}
                caseId={caseId}
                starterPayload={starterPayload}
                starterMessage={starterMessage}
                onStarterMessageConsumed={onStarterMessageConsumed}
                onCaseCreated={onCaseCreated}
                onNewConversation={onNewConversation}
                onMinimize={onMinimize}
                onClose={onClose}
                onUnreadMessage={onUnreadMessage}
                onLatestAssistantMessageChange={onLatestAssistantMessageChange}
              />
            )}
          </SessionProvider>
        </QueryClientProvider>
      </trpc.Provider>
    </Sentry.ErrorBoundary>
  )
}
